import { PageLocaleEnum } from 'common/enums/PageLocaleEnum'
import React, { useMemo, useState } from 'react'
import { DeletePageModal } from 'src/editor/layout/sidebar/pages-management/components/page-element-with-actions/delete-page-modal'
import { PublishPageModal } from 'src/editor/layout/sidebar/pages-management/components/page-element-with-actions/publish-page-modal'
import { UnpublishPageModal } from 'src/editor/layout/sidebar/pages-management/components/page-element-with-actions/unpublish-page-modal'
import { CheckCircleIcon } from 'src/editor/shared/icons/check-circle-icon'
import { DotsVerticalIcon } from 'src/editor/shared/icons/dots-vertical-icon'
import { Settings02Icon } from 'src/editor/shared/icons/settings-02-icon'
import { XCircleIcon } from 'src/editor/shared/icons/x-circle-icon'
import { XCloseIcon } from 'src/editor/shared/icons/x-close-icon'
import { ActionMenu } from 'src/editor/shared/ui-lib/action-menu'
import { Badge } from 'src/editor/shared/ui-lib/badge'
import { GhostIconButton } from 'src/editor/shared/ui-lib/buttons'
import {
  PageElement,
  PageElementProps,
} from 'src/editor/shared/ui-lib/page-element'
import { WebsiteSettings } from 'src/editor/shared/ui-lib/website-settings'
import { PageActionsEnum } from 'src/shared/enums/website-enums'
import { useTypedTranslation } from 'src/shared/i18next/hooks/use-typed-translation'
import { WebsitePageLocale } from 'src/shared/types/website-page-interface'
import { pageActionsExclusions } from '../../constants/page-actions-exclusions'
import { pageRemovalExclusions } from '../../constants/page-removal-exclusions'

export interface PageElementWithActionsProps
  extends Omit<
    PageElementProps,
    'endElement' | 'onClick' | 'isChecked' | 'onClick' | 'isActive'
  > {
  websiteId: number
  websitePageSetId: number
  websitePageId: number
  pageId: number
  isPublished: boolean
  locales: WebsitePageLocale[]
  websitePageLocale: PageLocaleEnum
  isActive?: boolean
  onClick?: (type: string) => void
}

export const PageElementWithActions = ({
  websiteId,
  websitePageSetId,
  websitePageId,
  pageType,
  pageId,
  isPublished,
  ...props
}: PageElementWithActionsProps) => {
  const { t } = useTypedTranslation()

  const [openedAction, setOpenedAction] = useState<PageActionsEnum | null>(null)

  const items = useMemo(
    () => [
      {
        id: PageActionsEnum.Settings,
        label: t('website_settings.sidebar.pages_management.settings'),
        iconBefore: () => <Settings02Icon />,
      },
      {
        id: PageActionsEnum.Publish,
        label: t('website_settings.sidebar.pages_management.publish'),
        iconBefore: () => <CheckCircleIcon />,
        isHidden: isPublished,
      },
      {
        id: PageActionsEnum.Unpublish,
        label: t('website_settings.sidebar.pages_management.unpublish'),
        iconBefore: () => <XCircleIcon />,
        isHidden: !isPublished,
      },
      {
        id: PageActionsEnum.Remove,
        label: t('components.core.actions.remove'),
        iconBefore: () => <XCloseIcon />,
        isHidden: pageRemovalExclusions.includes(pageType),
      },
    ],
    [t, isPublished],
  )

  return (
    <PageElement
      endElement={
        <div className="flex items-center gap-2">
          {!isPublished && (
            <Badge className="shrink-0" withCircle>
              {t('website_settings.sidebar.pages_management.unpublished_badge')}
            </Badge>
          )}
          {!pageActionsExclusions.includes(pageType) && (
            <ActionMenu
              items={items}
              onSelect={setOpenedAction}
              triggerElement={setIsOpened => (
                <GhostIconButton
                  className="shrink-0"
                  onClick={() => setIsOpened(true)}
                  icon={<DotsVerticalIcon className="fill-iconSecondary" />}
                />
              )}
            />
          )}
          {openedAction === PageActionsEnum.Settings && (
            <WebsiteSettings
              pageType={pageType}
              websitePageSetId={websitePageSetId}
              websitePageId={websitePageId}
              pageId={pageId}
              isOpened={openedAction === PageActionsEnum.Settings}
              onClose={() => setOpenedAction(null)}
            />
          )}
          {openedAction === PageActionsEnum.Publish && (
            <PublishPageModal
              websiteId={websiteId}
              websitePageSetId={websitePageSetId}
              websitePageId={websitePageId}
              isOpened={openedAction === PageActionsEnum.Publish}
              onClose={() => setOpenedAction(null)}
            />
          )}
          {openedAction === PageActionsEnum.Unpublish && (
            <UnpublishPageModal
              websiteId={websiteId}
              websitePageSetId={websitePageSetId}
              websitePageId={websitePageId}
              isOpened={openedAction === PageActionsEnum.Unpublish}
              onClose={() => setOpenedAction(null)}
            />
          )}
          {openedAction === PageActionsEnum.Remove && (
            <DeletePageModal
              websiteId={websiteId}
              websitePageSetId={websitePageSetId}
              websitePageId={websitePageId}
              websitePageLocale={props.websitePageLocale}
              pageName={props.name}
              isOpened={openedAction === PageActionsEnum.Remove}
              onClose={() => setOpenedAction(null)}
              locales={props.locales}
            />
          )}
        </div>
      }
      pageType={pageType}
      {...props}
    />
  )
}
